import { Fragment, useEffect, useLayoutEffect, useRef, useState } from 'react'
import {
	AppButton,
	DotMatrix,
	Input,
	Modal,
	useEnterExit
} from '@clickguard/design-system'
import AlertCircle from '@untitled-ui/icons-react/build/esm/AlertCircle'
import CheckCircle from '@untitled-ui/icons-react/build/esm/CheckCircle'
import Check from '@untitled-ui/icons-react/build/esm/Check'
import PlayCircle from '@untitled-ui/icons-react/build/esm/PlayCircle'
import XClose from '@untitled-ui/icons-react/build/esm/XClose'
import {
	BotLevel,
	CaptchaTrigger,
	CrmConnections,
	NewFormDraft,
	botLevelPatch,
	emailDefaults,
	reachEstimate,
	useFormSpam
} from '../form-spam-data'
import {
	AuthorizeDialog,
	BotStep,
	CONFIRM_MODAL_WIDTH,
	CrmStep,
	DialogHeader,
	EmailStep,
	RequestSourceDialog,
	RequestState,
	WidgetStep,
	botConsentNeeded,
	useAuthorization,
	useCrmConnect,
	useCrmField,
	useWidgetCheck
} from './form-settings-shared'
import { DiscardDraftDialog } from './form-setup-panes'
import { StepList } from './step-list'
import './link-form-modal.css'

interface LinkFormModalProps {
	open: boolean
	onClose: () => void
	onLink: (draft: NewFormDraft) => void
	/* The /empty route pins the first-run view while the store still holds its
	   seeded forms, so the modal there must not read "has forms" off the store —
	   it would open without the video header on the one page built to show it. */
	forceFirstRun?: boolean
}

type VerifyState = 'idle' | 'checking' | 'detected' | 'missing'

/**
 * R81: a numbered step can hold more than one PAGE.
 *
 * His words: "Email validation should have step A and B - Email and CRM. Same
 * with bot protection - A is the widget and b is the options", then "like
 * different 'pages' in the modal but the step is still the same".
 *
 * So the flow runs on five pages, and the tray still runs on three steps. Next
 * moves one page. The circle and the label do not change between A and B: the
 * step IS the same. The rule that leaves the current circle fills to half on
 * page B, which is the only mark the tray makes of the two pages.
 *
 * The wizard state is the PAGE index. Read the step from this table, never the
 * other way round: two pages share a step, so a step cannot name a page.
 *
 * R144: step 3 runs the other way round from his 2026 quote above. A is the
 * options now and B is the widget. See BOT_PAGE.
 */
const PAGES: { step: number; part: number }[] = [
	{ step: 0, part: 0 },
	{ step: 1, part: 0 },
	{ step: 1, part: 1 },
	{ step: 2, part: 0 },
	{ step: 2, part: 1 }
]
const PAGE_COUNT = PAGES.length
/** Where a tray click lands: the FIRST page of the step the user picked. */
const firstPageOf = (step: number) => PAGES.findIndex((p) => p.step === step)

/**
 * The CRM page, page B of step 2. R99 gates its Next on a connection and gives
 * it a ghost skip, so the footer must know which page it stands on. Read the
 * index from the table, never write a literal 2: a new page in front of it
 * would move it.
 */
const CRM_PAGE = PAGES.findIndex((p) => p.step === 1 && p.part === 1)

/**
 * R144 (Gustavo, pin i33t5u; Esteban, 2026-08-28: "just make the setup be
 * before the widget"): the bot options are page A of step 3 and the widget is
 * page B, the LAST page. R69 had put the widget first, because the widget must
 * sit on the page before any choice behind it reaches a visitor. Gustavo's
 * point wins: a user who is asked to paste a snippet, and only THEN asked if
 * they want the feature at all, has done work the next page can undo.
 */
const BOT_PAGE = PAGES.findIndex((p) => p.step === 2 && p.part === 0)

/** The gutter the overlay keeps above and below the card, top and bottom. */
const OVERLAY_ROOM = 64
/**
 * The body never shrinks past this, even on a very short window.
 *
 * It is 120, not 220. The floor beats the true room only on a short window,
 * and that is the one case where a tall card does real harm: the DS `Modal`
 * sets no max height, so a card taller than the window makes the OVERLAY
 * scroll, and the user gets an outer scrollbar and an inner one at once. The
 * run with the promo video carries about 200px more chrome than the run
 * without it, so a 220 floor overflowed every window under about 720px tall.
 * A 13-inch MacBook is one. A 120 floor still holds a readable strip, and it
 * keeps the head, the tray and the footer on the screen, which is the whole
 * job of the clamp.
 */
const BODY_MIN = 120
/**
 * R82: the sheet never runs taller than this, even on a tall window.
 *
 * "Since the modal is scrollable i think this can be a bit shorter man." The
 * window room alone gave the Email page a 720px sheet on a 900px window, so the
 * card ran 771px tall and reached both rims. The sheet already scrolls, so the
 * extra height buys nothing.
 *
 * 440 is measured, not picked: the four other pages measure 222, 360, 383 and
 * 434 of content, so 440 clears every one of them and clamps ONLY the Email
 * page. The tallest card then lands at 556px, which is the same height the bot
 * options page reaches on its own. Every page now sits in one band.
 */
const SHEET_MAX = 440

/* How long the OUTGOING step is held over the incoming one. It must match the
   `.lfm-step-ghost` exit in the CSS, because it is also the timer that unmounts
   the ghost. R122 cut it from 180 to 120: the two halves are staggered now, so
   the ghost only has to survive its own 110ms exit, not the whole swap. */
const STEP_MS = 120

// The header retells each PAGE in the same voice as the page content. R81
// split the two long steps in half, so each head now carries one job instead
// of two joined by a comma.
const PAGE_HEADERS: { title: string; desc: string }[] = [
	{
		/* R100: 'Link a form' in BOTH runs now. The intro in front of this page
		   carries "Link your first form", and the same title twice in a row reads
		   as a page that never moved. */
		title: 'Link a form',
		desc: 'Point ClickGuard at a form on your site. It scores each submission and blocks the spam.'
	},
	{
		/* R124 (Gustavo): the page says what the check DOES for the reader's
		   CRM, and when it runs. */
		title: 'Flag bad submissions on your CRM',
		desc: "Check the validity of an email address at the moment it's entered into your lead forms."
	},
	{
		/* R118: the CRM page is the one page a user can pass straight through,
		   and the header never said so. A user who has no CRM read the step as a
		   wall. The second sentence names both halves: they can leave it, and we
		   still want them to do it. */
		title: 'Send the leads to your CRM',
		desc: 'The addresses that pass go to the tools you already use. This step is optional, but we recommend it.'
	},
	{
		/* R124 (Gustavo): the same noun as the tray label and the list column. */
		title: 'Bot verification',
		desc: 'Choose what we do when a submission scores as a bot.'
	},
	{
		title: 'Install the widget',
		desc: 'It scores each visitor before the form is sent. It is one line of HTML.'
	}
]

// The tray labels. Short enough to sit BESIDE a circle, three across, on a
// 520px header row with a rule in each gap.
const STEP_LABELS = ['Form details', 'Email validation', 'Bot verification']

/**
 * R100, the INTRO. It stands in front of page 0, and only the first form a
 * workspace links ever sees it.
 *
 * His words, 2026-08-25: "we have the video on the top inside the white card.
 * We do not see the form details, email, or more protection steps up top. And
 * there are a few bullet point steps explaining what the user will do when
 * linking a new form."
 *
 * So the promo band leaves the card header and becomes the top of the SHEET,
 * the tray collapses to nothing, and the body carries the three things the user
 * is about to do.
 *
 * The index is -1, not 5. Every page test in this file reads `page === 0`,
 * `page === CRM_PAGE` or `page === PAGE_COUNT - 1`, and a page that stands in
 * FRONT of the flow has to fail all three without one of them being rewritten.
 * A negative index does that on its own. `PAGES[-1]` is undefined, so anything
 * that reads the table clamps with `Math.max(page, 0)`.
 */
const INTRO = -1

/**
 * R159 (Gustavo, pin #50): the tutorial video. The official ClickGuard
 * walkthrough on YouTube. The embed keeps the player's own controls, so
 * pause, seek, volume and fullscreen belong to the player, not to us.
 */
const TUTORIAL_EMBED = 'https://www.youtube.com/embed/rr8CuDYIxJ0?rel=0'
const TUTORIAL_TITLE = 'CLICK FRAUD Protection for Google Ads by ClickGUARD'

const INTRO_HEAD = {
	title: 'Link your first form',
	// R156 (Gustavo, pin #52): his copy, verbatim.
	desc: 'This is how the process is going to look like:'
}

/**
 * The three things the user does next, numbered the way the tray numbers its
 * steps. The intro draws the map inside the sheet, then the tray takes the map
 * over on page 0, so the numbers carry straight across.
 *
 * One line each on a 560px card. Two-line rows push the sheet past SHEET_MAX
 * and the intro starts to scroll, which costs the user the frosted close.
 */
const INTRO_STEPS = [
	// R156 (Gustavo, pin #52): his copy, verbatim. Step 3 runs to two lines;
	// measured against SHEET_MAX in the browser on 2026-08-31.
	'Name the form and the URL where it is located',
	'Choose what ClickGuard must do when bad submissions are identified',
	'(optional but recommended): install our smart CAPTCHA and protect your form against submissions from bots and AI agents'
]

/**
 * R69, the step tray. It replaces the four dots that used to sit in the footer
 * corner: they carried no names, and the user could not see how far the flow
 * went.
 *
 * DS GAP TO REPORT, do not patch: the DS has no stepper. `cg-ds-inventory`
 * returns `ProgressBar` and `StepPreviewCard`, and neither one is this.
 *
 * TRAP: this must stay OUTSIDE `.lfm-steps`. That block measures an exact
 * height, clips with overflow: hidden, cross-fades a ghost copy of itself on
 * every Next, and starts its children at `opacity: 0` through
 * `animation: ... both`. A tray inside it would be drawn twice, would slide
 * with the step, and would render blank if the animation never ran. Navigation
 * is visible by default, always.
 */
const StepTray = ({
	step,
	part,
	onGo,
	disabled
}: {
	step: number
	part: number
	onGo: (next: number) => void
	disabled: boolean
}) => (
	/* R71: the stepper sits straight on the tray, with no band of its own. The
	   two hairlines and the fill left with it: on a grey ground a filled band
	   would draw a THIRD surface, and the card only has two.
	   R73: it takes the same padding on all four sides that the footer takes,
	   so the two tray bands sit on one frame.
	   R78: 12px, at his ask. The sheet keeps its 16px.
	   R80: 44px on the right, which is the 12px plus the 24px close plus an
	   8px gap. The close moved into this band, and the last label must clear
	   it. Do not drop this padding without moving the close back.

	   R77: it reads as the HEADER of the card. The circle and the label sit on
	   ONE line, so the band is 56px tall and runs left to right the way a header
	   does, not 76px tall with the names stacked under the circles.

	   The measure ties it to the footer: both tray bands take 12px inside the
	   frame's 4px, so the first circle starts on the same x as the Back button
	   and the last label ends on the same x as the Next button. The sheet keeps
	   16px and a hairline, so its title sits 5px further in. Do not centre this
	   row: centring breaks the measure and the band floats over the card. */
	<nav aria-label="Wizard steps" className="flex items-center p-3 pr-11">
		{STEP_LABELS.map((label, i) => {
			const done = i < step
			const current = i === step
			return (
				<Fragment key={label}>
					{/* One rule for each gap, and each one is a full track of its own.
					    The fill is a single width transition to 100%, so no cap ever
					    changes shape and no fill stops part way. */}
					{i > 0 && (
						<div className="pointer-events-none mx-3 h-0.5 min-w-[12px] flex-1 overflow-hidden rounded-full bg-[rgb(var(--cg-border))]">
							<div
								className="h-full rounded-full bg-[rgb(var(--cg-brand))] transition-[width] duration-200 ease-out motion-reduce:transition-none"
								/* R81: half on page B of the current step. The circle and the
								   label hold still, because the step holds still. */
								style={{ width: step >= i ? '100%' : i === step + 1 && part === 1 ? '50%' : '0%' }}
							/>
						</div>
					)}
					<button
						type="button"
						disabled={i >= step || disabled}
						onClick={() => i < step && !disabled && onGo(i)}
						aria-current={current ? 'step' : undefined}
						aria-label={`Step ${i + 1}, ${label}`}
						className="flex shrink-0 items-center gap-2 rounded-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-brand-tint-text"
					>
						<span
							className={`relative flex h-6 w-6 shrink-0 items-center justify-center rounded-full border transition-[background-color,border-color] duration-200 motion-reduce:transition-none ${
								done || current
									? 'border-transparent bg-[rgb(var(--cg-brand))]'
									: 'border-[rgb(var(--cg-border))] bg-[rgb(var(--cg-surface))]'
							}`}
						>
							{/* The number and the check cross-fade in place. Neither one
							    hard-cuts, and the circle never changes size. */}
							<span
								className={`absolute text-[12px] font-semibold leading-[16px] transition-[opacity,transform] duration-200 motion-reduce:transition-none ${
									done ? 'scale-75 opacity-0' : 'scale-100 opacity-100'
								} ${current ? 'text-[rgb(var(--cg-text-on-brand))]' : 'text-[rgb(var(--cg-text-tertiary))]'}`}
							>
								{i + 1}
							</span>
							<Check
								aria-hidden="true"
								className={`absolute h-3.5 w-3.5 text-[rgb(var(--cg-text-on-brand))] transition-[opacity,transform] duration-200 motion-reduce:transition-none ${
									done ? 'scale-100 opacity-100' : 'scale-75 opacity-0'
								}`}
							/>
						</span>
						{/* R97, at his ask: one step up from 12px. 14px on a 20px line
						    still sits inside the 24px circle, so the tray height and
						    the whole measure hold. */}
						<span
							className={`whitespace-nowrap text-[14px] leading-[20px] transition-colors duration-200 motion-reduce:transition-none ${
								current
									? 'font-medium text-[rgb(var(--cg-text-primary))]'
									: done
									? 'text-[rgb(var(--cg-text-secondary))]'
									: 'text-[rgb(var(--cg-text-tertiary))]'
							}`}
						>
							{label}
						</span>
					</button>
				</Fragment>
			)
		})}
	</nav>
)

const slugify = (value: string) =>
	value
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, '-')
		.replace(/^-+|-+$/g, '')
		.slice(0, 40) || 'form'

/**
 * R159 (Gustavo, pin #50): the tutorial in its own modal, over the wizard.
 * The old inline promo was a muted loop with no controls. This is the
 * official ClickGuard walkthrough on YouTube, in the player's own chrome,
 * so play, pause, seek, volume and fullscreen all work. Same siting rules
 * as RequestSourceDialog: a SIBLING of the wizard card, zIndex 70 over the
 * wizard's 60. It unmounts on close, and that is what stops the playback.
 */
const TutorialDialog = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
	const modal = useEnterExit(open, { duration: 200 })
	if (!modal.mounted) return null
	return (
		<Modal
			open
			onClose={onClose}
			size="md"
			rounded="xl"
			zIndex={70}
			className={`!w-[640px] !max-w-[calc(100vw-48px)] !animate-none transition-[opacity,transform] duration-200 motion-reduce:transition-none ${
				modal.entered ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0'
			}`}
			overlayClassName={`!animate-none transition-opacity duration-200 motion-reduce:transition-none ${
				modal.entered ? 'opacity-100' : 'opacity-0'
			}`}
		>
			<div className="flex flex-col gap-4 p-4">
				<DialogHeader
					title="Watch tutorial"
					subtitle="The ClickGuard walkthrough on YouTube."
					onClose={onClose}
				/>
				<iframe
					className="aspect-video w-full rounded-lg border-0 bg-[#101828]"
					src={TUTORIAL_EMBED}
					title={TUTORIAL_TITLE}
					allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
					allowFullScreen
				/>
			</div>
		</Modal>
	)
}

export const LinkFormModal = ({ open, onClose, onLink, forceFirstRun = false }: LinkFormModalProps) => {
	const { mounted, entered } = useEnterExit(open, { duration: 200 })

	/* The intro teaches what linking is. A workspace that already holds a form
	   does not need the lesson again, so the modal opens straight on page 0
	   with no intro. R159 (Gustavo, pin #50): the video lives in TutorialDialog
	   now, behind the footer's `Watch tutorial`, on every page. The read is
	   frozen while the modal stands: onLink adds the row before the exit
	   finishes, so a live read would drop the intro mid-exit. */
	const { forms, crmAccounts, addCrmAccount } = useFormSpam()
	const firstRunRef = useRef(forms.length === 0)
	if (!mounted) firstRunRef.current = forceFirstRun || forms.length === 0
	const firstRun = firstRunRef.current

	const [page, setPage] = useState(firstRunRef.current ? INTRO : 0)
	const intro = page === INTRO

	/* R159 (Gustavo, pin #50): `Watch tutorial` opens the video in its own
	   modal over the wizard, so the page under it never changes. */
	const [tutorial, setTutorial] = useState(false)

	/* The tray is gone on the intro, and it grows in as the flow starts. */
	const tray = useEnterExit(!intro, { duration: 200 })

	/* R122: the pin that holds the tray open on the first frame of a run.
	   `useEnterExit` reports `entered` two frames late, and the modal is
	   already animating in under it, so the band the modal OPENS on must not
	   play a 0 -> full grow on that frame. That reads as a jump. The latch
	   closes the moment the tray has entered once, so only the open frame is
	   ever pinned. The ref mutates in render the way `firstRunRef` does. */
	const trayPinned = useRef(!firstRun)
	if (!mounted) trayPinned.current = !firstRun
	if (tray.entered) trayPinned.current = false

	const [ghostPage, setGhostPage] = useState<number | null>(null)
	const ghostTimer = useRef<number>()

	const [name, setName] = useState('')
	const [url, setUrl] = useState('')
	const [verify, setVerify] = useState<VerifyState>('idle')

	// A new form watches before it acts. Ralph asked for monitoring first, so
	// the wizard never links a form that challenges or blocks a visitor unless
	// the user chose that degree and agreed to it here.
	const [botMaster, setBotMaster] = useState(true)
	const [botLevel, setBotLevel] = useState<BotLevel>('monitor')
	// R69: WHO the action reaches. It stays `suspected`, which is what the
	// wizard has always written. `high-only` here would quietly change the
	// form every user gets by default.
	const [botTrigger, setBotTrigger] = useState<CaptchaTrigger>('suspected')
	// The widget is the only part the visitor meets. Until the user says it is
	// on the page, the form is linked but not protected, and the list row says
	// "Needs setup".
	// R85: the widget check replaced the "I added this to my form." checkbox.
	const { widgetState, checkWidget, resetWidget } = useWidgetCheck()
	// R49: the user's word for the degree. Every change of degree, and every
	// return of the master, asks for it again, so a yes never carries over.
	const [botConsent, setBotConsent] = useState(false)

	const pickMaster = (next: boolean) => {
		setBotMaster(next)
		setBotConsent(false)
	}

	const pickLevel = (next: BotLevel) => {
		setBotLevel(next)
		setBotConsent(false)
	}

	const [emailMaster, setEmailMaster] = useState(true)
	const [emailOn, setEmailOn] = useState<Record<string, boolean>>(emailDefaults)

	// The login the popup returns joins the workspace list, and this draft
	// picks it. A later form can pick the same account from the row's list.
	const [picked, setPicked] = useState<CrmConnections>({ brevo: null, hubspot: null })
	const { crm, connectCrm, reset: resetCrm } = useCrmConnect((provider, account) => {
		const held = addCrmAccount({ provider, ...account })
		setPicked((prev) => ({ ...prev, [provider]: held.id }))
	})

	// Ralph, 2026-08-20: we never create the CRM field. The wizard names it and
	// checks for it. The provider is derived from the rows, so a disconnect
	// takes the panel away with the connection.
	const { fieldState, checkField, resetField } = useCrmField()
	const fieldProvider = picked.brevo ? 'brevo' : picked.hubspot ? 'hubspot' : null

	// R152 (audit 2026-08-28): the check result is one flag, not one per
	// provider. A second connection flips the panel to the other CRM's recipe,
	// and a `found` from the first CRM read as found there too. A provider
	// change starts the check over.
	useEffect(() => {
		resetField()
		// eslint-disable-next-line
	}, [fieldProvider])

	// The gate in front of the CRM connection. R49 took the bot degrees off it:
	// they ask on the consent row inside the card, not in a dialog over it.
	// R247: the CRM Connect goes straight to the OAuth popup; nothing opens the ask here now.
	const { ask, closeAsk } = useAuthorization()

	// "Link form" plays a creating step first: the body crossfades to the
	// loader, then onLink adds the row and the page opens the drawer.
	const [creating, setCreating] = useState(false)
	const createTimer = useRef<number>()

	// "Request a source": the row opens a second modal with the one-field
	// ask. Send closes it, the row shows Sending…, then Request sent.
	const [request, setRequest] = useState<RequestState>('idle')
	const [requestName, setRequestName] = useState('')
	const requestTimer = useRef<number>()

	// Escape and an overlay click ask before they throw the draft away.
	const [confirmDiscard, setConfirmDiscard] = useState(false)

	// Reset to a fresh draft every time the modal opens.
	useEffect(() => {
		if (!open) {
			// R152 (audit 2026-08-28): a Discard promised every setting goes
			// away, but the OAuth popup stayed open and its login still landed
			// in the workspace list. The close path shuts the popup and stops
			// the timers; the open path below rebuilds the draft.
			resetCrm()
			window.clearTimeout(createTimer.current)
			window.clearTimeout(requestTimer.current)
			return
		}
		// R100: a first run opens on the intro, every other run on page 0. The
		// ref is already frozen here: it is written on the last closed render.
		setPage(firstRunRef.current ? INTRO : 0)
		setTutorial(false)
		setGhostPage(null)
		setName('')
		setUrl('')
		setVerify('idle')
		setBotMaster(true)
		setBotLevel('monitor')
		setBotConsent(false)
		setEmailMaster(true)
		setEmailOn(emailDefaults())
		setRequest('idle')
		setRequestName('')
		setCreating(false)
		setConfirmDiscard(false)
		closeAsk()
		window.clearTimeout(createTimer.current)
		window.clearTimeout(requestTimer.current)
		window.clearTimeout(ghostTimer.current)
		resetCrm()
		setPicked({ brevo: null, hubspot: null })
		resetField()
		setBotTrigger('suspected')
		resetWidget()
		// eslint-disable-next-line
	}, [open])

	// Demo backend check: debounce the typed URL, then "check" it. A URL that
	// contains "fail" demos the not-detected branch.
	useEffect(() => {
		if (!open) return
		const value = url.trim()
		setVerify('idle')
		if (!value) return
		let check: number | undefined
		const debounce = window.setTimeout(() => {
			setVerify('checking')
			check = window.setTimeout(() => {
				setVerify(value.toLowerCase().includes('fail') ? 'missing' : 'detected')
			}, 1500)
		}, 600)
		return () => {
			window.clearTimeout(debounce)
			if (check) window.clearTimeout(check)
		}
	}, [url, open])

	useEffect(
		() => () => {
			window.clearTimeout(ghostTimer.current)
			window.clearTimeout(requestTimer.current)
			window.clearTimeout(createTimer.current)
		},
		[]
	)

	const sendRequest = () => {
		if (!requestName.trim()) return
		setRequest('sending')
		requestTimer.current = window.setTimeout(() => setRequest('sent'), 1400)
	}

	// The body region animates between step heights; the outgoing step stays
	// mounted as a ghost while its exit plays.
	const bodyRef = useRef<HTMLDivElement>(null)
	const [bodyHeight, setBodyHeight] = useState<number>()
	useLayoutEffect(() => {
		const el = bodyRef.current
		if (!el) return
		// R80: the sheet holds the scroll position, and it outlives the step. A
		// user who reads to the end of step 2 and presses Next would open step 3
		// on its own middle. Send it back to the top on every swap.
		const sheetEl = el.closest('.lfm-sheet')
		if (sheetEl) sheetEl.scrollTop = 0
		// The room the body has left. It reads the CHROME only, so it never
		// touches `bodyHeight` and cannot feed the height animation back into
		// itself. That split is what lets the card observer below be safe.
		const fit = () => {
			// R69: hand the CSS the room the body has left, so the head, the tray
			// and the footer never leave the screen. The run with the promo video
			// has 200px more chrome than the run without it, so a fixed vh cannot
			// serve both.
			//
			// TRAP: do NOT read the chrome as `card.offsetHeight - el.offsetHeight`.
			// The card's height comes from `.lfm-steps`, which carries the height
			// of the PREVIOUS render in `bodyHeight`, so that subtraction feeds
			// its own lag back into the next room and settles on a wrong number.
			// The two gaps below are true distances instead: everything above the
			// body, and everything under it. Neither one moves when the body
			// grows, so one pass is enough. Measured, not inferred: the first
			// version reported a 264px chrome as 24px and let the card run to
			// 1096px inside a 640px window.
			//
			// R80: it measures the SHEET, not the body block. The sheet is the
			// scroller now, so the head scrolls away with the body it belongs to.
			// The two gaps stay true distances: the header band above the sheet,
			// and the footer under it. Neither moves when the sheet grows, so the
			// invariance the card observer below depends on still holds.
			const card = el.closest('.lfm-card') as HTMLElement | null
			const sheet = el.closest('.lfm-sheet')
			if (card && sheet) {
				const cardBox = card.getBoundingClientRect()
				const sheetBox = sheet.getBoundingClientRect()
				const chrome = sheetBox.top - cardBox.top + (cardBox.bottom - sheetBox.bottom)
				// R82: the window room is a CEILING, not a target. `SHEET_MAX` holds
				// the card to one band on a tall window, and `BODY_MIN` still wins on a
				// short one, so the order is min first and max last.
				const room = Math.max(
					BODY_MIN,
					Math.min(SHEET_MAX, window.innerHeight - chrome - OVERLAY_ROOM)
				)
				card.style.setProperty('--lfm-sheet-max', `${Math.round(room)}px`)
			}
		}
		const measure = () => {
			fit()
			setBodyHeight(el.offsetHeight)
		}
		measure()
		const observer = new ResizeObserver(measure)
		observer.observe(el)
		// The head, the tray and the promo video all sit ABOVE the body, so the
		// observer on the body never sees any of them move. The head and the
		// video each animate over 200ms, and each one changes the chrome: the
		// video alone is 200px of it. Without this second observer, a Next
		// pressed inside those 200ms fits the body to a part-grown chrome and
		// the wrong room holds for the whole step.
		//
		// The card is safe to watch BECAUSE `fit` is invariant to the body
		// height: `chrome` is the card box minus the frame box, and the two
		// grow by the same pixels, so they cancel. Only real chrome moves it.
		const chromeObserver = new ResizeObserver(fit)
		const cardEl = el.closest('.lfm-card')
		if (cardEl) chromeObserver.observe(cardEl)
		window.addEventListener('resize', measure)
		return () => {
			observer.disconnect()
			chromeObserver.disconnect()
			window.removeEventListener('resize', measure)
		}
	}, [page, mounted, creating])

	// The header crossfades between per-page copy the same way, with its own
	// measured height so a one-line title swap never jumps the layout.
	const headRef = useRef<HTMLDivElement>(null)
	const [headHeight, setHeadHeight] = useState<number>()
	useLayoutEffect(() => {
		const el = headRef.current
		if (!el) return
		const measure = () => setHeadHeight(el.offsetHeight)
		measure()
		const observer = new ResizeObserver(measure)
		observer.observe(el)
		return () => observer.disconnect()
	}, [page, mounted, creating])

	/* R100: the tray collapses to nothing on the intro, so it needs a number to
	   collapse FROM. It is measured and not written as 48, because the band is
	   its own padding plus a 24px circle plus a 20px label, and every one of the
	   three has already moved at his ask (R77, R78, R97). A literal would break
	   quietly on the next one. `undefined` until the first measure means `height:
	   auto`, which is the same pixel, so nothing jumps on the way in.

	   R122: `mounted` belongs in the deps, the way the head and the body above
	   already have it. `tray.mounted` reads `!intro`, and on a run that opens on
	   a step that is true from the FIRST render of this component, while the
	   modal is still shut and the body is still `null`. So the effect ran once
	   against a null ref, gave up, and never ran again: measured `trayHeight`
	   stayed `undefined` for the whole run. `height: auto` looks right until the
	   band has to leave, and no transition can run from `auto` to `0`, so the
	   band cut instead of collapsing. */
	const trayRef = useRef<HTMLDivElement>(null)
	const [trayHeight, setTrayHeight] = useState<number>()
	useLayoutEffect(() => {
		const el = trayRef.current
		if (!el) return
		const measure = () => setTrayHeight(el.offsetHeight)
		measure()
		const observer = new ResizeObserver(measure)
		observer.observe(el)
		return () => observer.disconnect()
	}, [tray.mounted, mounted])

	// Audit finding 9: both measured heights belong to the draft that just
	// closed. The DS `Modal` builds a fresh node on every open today, so a
	// stale number never paints, but the DS does not promise that. Clear the
	// two once the body unmounts, so the next open measures from nothing.
	useEffect(() => {
		if (mounted) return
		setBodyHeight(undefined)
		setHeadHeight(undefined)
	}, [mounted])

	const go = (next: number) => {
		if (next === page) return
		window.clearTimeout(ghostTimer.current)
		setGhostPage(page)
		setPage(next)
		ghostTimer.current = window.setTimeout(() => setGhostPage(null), STEP_MS)
	}

	const step1Ready = name.trim().length > 0 && verify === 'detected'

	// R69: the one number under the target row. `forceFirstRun` claims the user
	// has no forms while the store still holds the seeds, so the line must stay
	// away on that route or it cites forms the page says do not exist.
	const estimate = firstRunRef.current ? null : reachEstimate(forms, botTrigger)

	// R49: the bot step holds Next until the user agrees to the degree they
	// picked. Monitor asks for nothing, so it never blocks the way forward.
	const botStepReady = !botConsentNeeded(botMaster, botLevel) || botConsent

	// R69: the bot step is LAST now, so the consent gate sits on the last page.
	// R81: that is page 4, the options, and not page 3, the widget.
	// R144: the options moved in front of the widget, so the gate sits on
	// BOT_PAGE and the last page, the widget, is never gated.
	// R99: the CRM page gates Next on a connection. The user is not trapped:
	// the ghost `Skip for now` in the footer walks past it. A disabled Next
	// plus a live skip says "this setup is still pending" without a word of
	// help text, which is what the old one-button Skip could never say.
	const pageReady =
		intro
			? true
			: page === 0
			? step1Ready
			: page === CRM_PAGE
			? fieldProvider !== null
			: page === BOT_PAGE
			? botStepReady
			: true

	/**
	 * The one ghost control in the footer, left of Back. Two pages want it and
	 * they want different words: the CRM page walks past a pending connection,
	 * the bot page turns bot verification off and walks on to the widget.
	 *
	 * R99: the slot fades, so the word must NOT change while the fade runs, or
	 * the text hard-cuts inside its own exit. `ghostShown` drives the opacity
	 * and `ghostRef` holds the last live control, so a page that shows nothing
	 * keeps the old word on screen until the fade finishes.
	 */
	const ghost =
		page === CRM_PAGE
			? { label: 'Skip for now', onClick: () => go(page + 1) }
			: page === BOT_PAGE
			? // R144: the bot page is not the last page any more, so Skip cannot
			  // link the form. It does what the master switch does, off, and
			  // moves on. `pickMaster` also drops any consent, so a Block the
			  // user had agreed to never rides through a Skip. Link form on the
			  // last page then writes `captcha: false` from the master.
			  {
					label: 'Skip',
					onClick: () => {
						pickMaster(false)
						go(page + 1)
					}
			  }
			: null
	const ghostShown = ghost !== null
	const ghostRef = useRef(ghost)
	if (ghost) ghostRef.current = ghost
	const ghostSeen = ghostRef.current

	// R152 (audit 2026-08-28): the tray collapses over 200ms on "Watch
	// tutorial". It read `PAGES[Math.max(page, 0)]` on those frames, so the
	// check marks fell back to numbers mid-collapse. The ref holds the last
	// page that was not the intro, the same way `ghostRef` holds the control.
	const trayPageRef = useRef(Math.max(page, 0))
	if (page !== INTRO) trayPageRef.current = page
	const trayPage = PAGES[trayPageRef.current]

	// The fresh draft to measure against. The defaults never change, so one
	// record for the life of the modal is enough.
	const emailBase = useRef(emailDefaults())
	const dirty =
		name.trim().length > 0 ||
		url.trim().length > 0 ||
		page > 0 ||
		!botMaster ||
		botLevel !== 'monitor' ||
		botTrigger !== 'suspected' ||
		widgetState !== 'idle' ||
		!emailMaster ||
		crm.brevo !== 'idle' ||
		crm.hubspot !== 'idle' ||
		picked.brevo !== null ||
		picked.hubspot !== null ||
		Object.keys(emailOn).some((key) => emailOn[key] !== emailBase.current[key])

	// One door out of the wizard. A blank draft closes at once; a started one
	// asks first, so Escape and an overlay click can no longer drop the work.
	const requestClose = () => {
		if (creating) return
		if (!dirty) {
			onClose()
			return
		}
		setConfirmDiscard(true)
	}

	/**
	 * Write the form and close.
	 *
	 * R69 gave this a `skipBot` flag: Skip on the last page meant "link it,
	 * leave the screening off", and it had to WRITE that. R144 moved the bot
	 * page in front of the widget, so Skip turns the master off and walks on,
	 * and the one write here reads the master like every other control.
	 */
	const submit = () => {
		if (creating) return
		window.clearTimeout(ghostTimer.current)
		setGhostPage(page)
		setCreating(true)
		ghostTimer.current = window.setTimeout(() => setGhostPage(null), STEP_MS)
		createTimer.current = window.setTimeout(() => {
			onLink({
				name: name.trim(),
				url: url.trim(),
				selector: `#${slugify(name)}`,
				settings: {
					captcha: botMaster,
					widget: widgetState === 'found',
					...botLevelPatch(botLevel, botTrigger),
					emailValidation: emailMaster,
					emailChecks: {
						syntax: emailOn.invalid,
						domain: emailOn.invalid,
						disposable: emailOn.doNotMail,
						roleBased: emailOn.doNotMail
					},
					emailRules: { ...emailOn },
					crm: { ...picked }
				}
			})
		}, 1600)
	}

	const renderHead = (index: number) => {
		const copy = index === INTRO ? INTRO_HEAD : PAGE_HEADERS[index]
		return (
			<div className="flex flex-col gap-1.5">
				<h2 className="text-[18px] font-semibold leading-[24px] cg-text-text-primary">{copy.title}</h2>
				<p className="text-[14px] leading-[20px] cg-text-text-secondary">{copy.desc}</p>
			</div>
		)
	}

	const creatingHead = (
		<div className="flex flex-col gap-1.5">
			<h2 className="text-[18px] font-semibold leading-[24px] cg-text-text-primary">Linking your form</h2>
			<p className="text-[14px] leading-[20px] cg-text-text-secondary">
				ClickGuard links the form and prepares its dashboard.
			</p>
		</div>
	)

	const creatingBody = (
		<div className="flex flex-col items-center gap-3 py-10" role="status">
			<DotMatrix size="md" label="Linking the form" />
			<p className="text-[14px] leading-[20px] cg-text-text-secondary">
				Linking <span className="font-medium cg-text-text-primary">{name.trim()}</span>…
			</p>
		</div>
	)

	const statusLayer = (active: boolean) =>
		`absolute inset-0 flex items-center gap-1.5 transition-opacity duration-200 motion-reduce:transition-none ${
			active ? 'opacity-100' : 'pointer-events-none opacity-0'
		}`

	const renderPage = (index: number) => {
		/* R100: the intro body. Three lines, numbered the way the tray numbers
		   its steps, in the quiet TINT of the brand and not the fill. The tray
		   itself owns the filled circle, and the intro must read as the preview
		   of that map, not as a second copy of it standing in the same card.
		   R111: `StepList` draws it now. */
		if (index === INTRO) {
			return <StepList items={INTRO_STEPS} />
		}
		if (index === 0) {
			return (
				<div className="flex flex-col gap-4">
					<div className="grid grid-cols-2 gap-4">
						<Input
							label="Form name"
							size="md"
							state="default"
							placeholder="Form name"
							value={name}
							onChange={(e) => setName(e.target.value)}
						/>
						<Input
							label="URL of the page with the form"
							size="md"
							placeholder="demo.example.com/demo"
							value={url}
							onChange={(e) => setUrl(e.target.value)}
							state={verify === 'missing' ? 'error' : verify === 'detected' ? 'success' : 'default'}
						/>
					</div>
					<div className="relative h-[24px]" aria-live="polite">
						<div className={statusLayer(verify === 'idle')}>
							<p className="text-[14px] leading-[20px] cg-text-text-tertiary">
								We check the page for your form when you enter the URL.
							</p>
						</div>
						<div className={statusLayer(verify === 'checking')}>
							{/* The 22px sm loader out-weighed the 20px CheckCircle it hands
							    over to. xs is 14px, which reads level with a line icon,
							    because a block of dots carries more weight per pixel. The
							    20px box holds the same slot as the icons, so the label does
							    not shift sideways as the states cross-fade. */}
							<span className="flex h-5 w-5 items-center justify-center">
								<DotMatrix size="xs" label="Checking the page" />
							</span>
							<p className="text-[14px] leading-[20px] cg-text-text-secondary">Checking…</p>
						</div>
						<div className={statusLayer(verify === 'detected')}>
							<CheckCircle className="h-5 w-5 cg-text-success" aria-hidden="true" />
							<p className="text-[14px] font-medium leading-[20px] cg-text-success">Form detected</p>
						</div>
						<div className={statusLayer(verify === 'missing')}>
							<AlertCircle className="h-5 w-5 text-[rgb(var(--fs-danger-ink))]" aria-hidden="true" />
							<p className="text-[14px] font-medium leading-[20px] text-[rgb(var(--fs-danger-ink))]">Form not detected</p>
							<span className="text-[14px] leading-[20px] cg-text-text-tertiary">·</span>
							<button type="button" className="cg-text-brand text-[14px] font-medium leading-[20px] hover:underline">
								See the docs
							</button>
							<span className="text-[14px] leading-[20px] cg-text-text-tertiary">·</span>
							<button type="button" className="cg-text-brand text-[14px] font-medium leading-[20px] hover:underline">
								Contact support
							</button>
						</div>
					</div>
				</div>
			)
		}
		if (index === 1) {
			// R69: the email rules and the CRM connect are one step. R81 gives each
			// one its own page inside that step. The retention banner and its
			// control left the wizard; the store defaults still write `90d`, and
			// the drawer still edits it.
			return (
				<EmailStep
					master={emailMaster}
					rules={emailOn}
					onMaster={setEmailMaster}
					onRule={(key, next) => setEmailOn((prev) => ({ ...prev, [key]: next }))}
				/>
			)
		}
		if (index === 2) {
			return (
				<CrmStep
					crm={crm}
					accounts={crmAccounts}
					picked={picked}
					onPickAccount={(provider, id) =>
						setPicked((prev) => ({ ...prev, [provider]: id }))
					}
					request={request}
					fieldProvider={fieldProvider}
					fieldState={fieldState}
					onCheckField={checkField}
					onConnect={connectCrm}
					onRequest={() => setRequest((prev) => (prev === 'idle' ? 'open' : prev))}
				/>
			)
		}
		if (index === BOT_PAGE) {
			// R69 put the widget snippet first, so the widget sat on the page
			// before any choice behind it reached a visitor. R144 (Gustavo, pin
			// i33t5u) turns that round: the user says what a bot gets, THEN pastes
			// the snippet, so nobody installs a widget for a feature they turn
			// off on the next page.
			return (
				<BotStep
					master={botMaster}
					level={botLevel}
					trigger={botTrigger}
					estimate={estimate}
					consent={botConsent}
					onMaster={pickMaster}
					onLevel={pickLevel}
					onTrigger={setBotTrigger}
					onConsent={setBotConsent}
				/>
			)
		}
		return (
			<WidgetStep
				action={slugify(name) || 'your-form'}
				state={widgetState}
				onCheck={checkWidget}
			/>
		)
	}

	if (!mounted) return null

	return (
		<>
		<Modal
			open
			onClose={requestClose}
			rounded="xl"
			// The layered asks own Escape while they stand: the request dialog
			// and the discard dialog each close themselves first.
			closeOnEscape={request !== 'open' && !ask && !confirmDiscard && !creating}
			closeOnOverlayClick={!ask && !confirmDiscard && !creating}
			/* R71: the card is the TRAY, not the sheet. `--cg-canvas` is the same
			   fill the DS `TrayCard` puts under its own content card, so the two
			   trays in the product read as one idea. The `!` is not decoration:
			   the DS `Modal` ships `cg-bg-surface`, and `twMerge` does not know
			   the `cg-` prefix, so both classes would survive and stylesheet
			   order would pick the winner. */
			className={`lfm-card !w-[560px] overflow-hidden !bg-[rgb(var(--cg-canvas))] !animate-none !shadow-[0px_32px_64px_-12px_rgba(16,24,40,0.14)] transition-[opacity,transform] duration-200 motion-reduce:transition-none ${
				entered ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0'
			}`}
			overlayClassName={`!animate-none transition-opacity duration-200 motion-reduce:transition-none ${
				entered ? 'opacity-100' : 'opacity-0'
			}`}
		>
			{/* R72: the frame takes NO gap. Every 16px sits on a piece. The card
			    runs in three bands: the stepper on the grey tray, the white sheet
			    with the head and the step body, and the footer back on the grey.
			    White is the content, grey is the tray.

			    R76: 4px of padding, at his ask. It is the only padding on the
			    frame. It insets all three bands from the card edge, so the grey
			    tray keeps a thin margin around the sheet. */}
			<div className="relative flex flex-col p-1">
				{/* R73: the stepper sits on the TRAY at the TOP, outside the white
				    sheet, the same way the footer buttons sit on the grey below. It
				    is the map of the flow, not a part of any one step.

				    R100: "We do not see the form details, email, or more protection
				    steps up top." The intro carries no map, so the band collapses to
				    nothing and unmounts, and the flow is not numbered until the user
				    is in it. The tray reads `trayPage` (R152), the last page that
				    was not the intro: the intro sits at -1, and the frames the tray
				    spends animating out must keep the marks of the page it left. */}
				{tray.mounted && (
					<div
						className="overflow-hidden transition-[height,opacity] duration-200 motion-reduce:transition-none"
						style={{
							height: tray.entered || trayPinned.current ? trayHeight : 0,
							opacity: tray.entered || trayPinned.current ? 1 : 0
						}}
					>
						<div ref={trayRef}>
							<StepTray
								step={trayPage.step}
								part={trayPage.part}
								onGo={(s) => go(firstPageOf(s))}
								disabled={creating}
							/>
						</div>
					</div>
				)}
				{/* The close.

				    R80: it sits on the FRAME, in the header band, NOT on the sheet.
				    The sheet is the scroller now, and an absolutely positioned child
				    of a scroll port travels with the scrolled content: on the sheet
				    this close rode the body up and left the card.

				    The measure: it sits 16px from the card edge, which is where the
				    frame's 4px plus the footer's 12px puts the Next button, and its
				    24px box centres on the 24px step circles. The offset is 16 and
				    not 12 because an absolutely positioned child resolves against
				    the frame's PADDING box, so the frame's own 4px does not count
				    twice. `StepTray` reserves the 32px this close takes. */}
				<button
					type="button"
					aria-label="Close"
					onClick={requestClose}
					tabIndex={creating ? -1 : undefined}
					className={`absolute right-4 top-4 z-10 flex h-6 w-6 items-center justify-center rounded-lg cg-text-text-tertiary transition-[opacity,background-color] duration-200 hover:bg-[rgb(var(--cg-hover-overlay)/var(--cg-hover-alpha))] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-tint-text motion-reduce:transition-none ${
						creating ? 'pointer-events-none opacity-0' : 'opacity-100'
					}`}
				>
					<XClose className="h-4 w-4" aria-hidden="true" />
				</button>
				{/* R73: the sheet. It runs the full width of the frame and holds
				    the head and the step body.

				    The padding sits HERE, never on `.lfm-steps`. That block carries
				    a -4px/-8px margin so a focus ring survives its clip, and its
				    ghost is absolutely positioned at left:0/right:0 against its own
				    padding box. Padding written on the block would move the ghost
				    and leave the head cross-fade misaligned by 16px.

				    R74: 12px, the same radius as the card. It runs to both frame
				    sides, so the grey tray shows through the four corners. The
				    shadow is the DS `TrayCard` INNER_SHADOW verbatim.

				    R75: a hairline, at his ask, in `--cg-border` (#e1e1e1). I tried
				    `--cg-subtle` (#ededed) first, to keep it away from the
				    `SettingsCard` line 16px inside it on steps 2 and 3. #ededed on
				    the #f6f6f6 tray is 9 levels of grey apart and reads as nothing,
				    so the lighter token fails the ask. */}
				<div className="lfm-sheet relative flex flex-col gap-4 rounded-lg border border-[rgb(var(--cg-border))] bg-[rgb(var(--cg-surface))] p-4">
				<div
					/* R80: no gutter. The close left the sheet for the header band, so
					   the head runs the full measure in both runs. */
					className="lfm-steps"
					style={{ height: headHeight }}
				>
					<div key={creating ? 'creating' : page} ref={headRef} className="lfm-step-enter">
						{creating ? creatingHead : renderHead(page)}
					</div>
					{ghostPage !== null && (
						<div className="lfm-step-ghost" aria-hidden="true">
							{renderHead(ghostPage)}
						</div>
					)}
				</div>
				<div className="lfm-steps" style={{ height: bodyHeight }}>
					<div key={creating ? 'creating' : page} ref={bodyRef} className="lfm-step-enter">
						{creating ? creatingBody : renderPage(page)}
					</div>
					{ghostPage !== null && (
						<div className="lfm-step-ghost" aria-hidden="true">
							{renderPage(ghostPage)}
						</div>
					)}
				</div>
				</div>
				{/* The whole footer fades while the form gets created: the step is
				    read-only, so no control may keep focus or clicks. */}
				<div
					/* R71: no pt-1. The gap-4 above it already is the gutter, and 4px
					   more would leave the sheet with 20px under it and 16px over it. */
					className={`flex w-full items-center gap-3 p-2 transition-opacity duration-200 motion-reduce:transition-none ${
						creating ? 'pointer-events-none opacity-0' : ''
					}`}
					aria-hidden={creating || undefined}
				>
					{/* R69: the four dots went to the tray at the top. The spacer stays,
					    or Skip, Back and Next all slide to the left edge.

					    R112: the tutorial stands in that spacer, at his ask. R159: it
					    opens the TutorialDialog, from every page, the intro too. */}
					<div className="flex flex-1 items-center gap-3">
						<AppButton
							variant="secondary"
							size="md"
							label="Watch tutorial"
							leftIcon={<PlayCircle className="h-4 w-4" />}
							onClick={() => setTutorial(true)}
							tabIndex={creating ? -1 : undefined}
						/>
					</div>
					<div
						className={`transition-opacity duration-200 motion-reduce:transition-none ${
							ghostShown ? 'opacity-100' : 'pointer-events-none opacity-0'
						}`}
					>
						{ghostSeen && (
							<AppButton
								variant="ghost"
								size="md"
								label={ghostSeen.label}
								onClick={ghostSeen.onClick}
								tabIndex={ghostShown && !creating ? undefined : -1}
							/>
						)}
					</div>
					{/* Back stays mounted on page 1 so its exit can play. */}
					<div
						className={`transition-opacity duration-200 motion-reduce:transition-none ${
							page > 0 ? 'opacity-100' : 'pointer-events-none opacity-0'
						}`}
					>
						<AppButton
							variant="secondary"
							size="md"
							label="Back"
							// R152 (audit 2026-08-28): the button stays mounted on page 0
							// for its exit, and a button that already holds focus fires on
							// Enter. `go(-1)` would land on the INTRO, so the floor is here.
							onClick={() => {
								if (page > 0) go(page - 1)
							}}
							tabIndex={page > 0 && !creating ? undefined : -1}
						/>
					</div>
					<AppButton
						variant="primary"
						size="md"
						/* R99: the CRM page says "Next" like every other page. R95 had it
						   swap to "Skip" while nothing was connected, so one button
						   carried both meanings. The ghost `Skip for now` carries the
						   skip now, and this button only ever goes forward. */
						/* R100: the intro says "Get started", not "Next". "Next" on a
						   page with nothing to fill in reads as a step the user
						   skipped, and this button starts the flow. */
						label={
							intro
								? 'Get started'
								: page === PAGE_COUNT - 1
								? 'Link form'
								: 'Next'
						}
						disabled={!pageReady}
						onClick={
							intro
								? () => go(0)
								: page === PAGE_COUNT - 1
								? () => submit()
								: () => go(page + 1)
						}
						tabIndex={creating ? -1 : undefined}
					/>
				</div>
			</div>
		</Modal>
		<RequestSourceDialog
			open={request === 'open'}
			value={requestName}
			onChange={setRequestName}
			onClose={() => setRequest('idle')}
			onSend={sendRequest}
		/>
		<AuthorizeDialog ask={ask} onClose={closeAsk} />
		<TutorialDialog open={tutorial} onClose={() => setTutorial(false)} />
		<DiscardDraftDialog
			open={confirmDiscard}
			onClose={() => setConfirmDiscard(false)}
			onConfirm={() => {
				setConfirmDiscard(false)
				onClose()
			}}
		/>
		</>
	)
}
